/**
 * 场景标注入口卡片 (v3)
 *
 * 布局与「场景批量生成」卡对齐 (顺序:Radio → 流水线 → 输入 → 保密 → 目标场景集 → 底部按钮),
 * 用于收集"批次级"标注参数 + 跳转到 /scene-annotate 逐条编辑。
 *
 * preview 拉 case 数据的动作在标注页做,本卡只负责入参收集。
 *
 * 分隔符支持:逗号 / 换行 / 空格,与「场景批量生成」一致。
 */
import { useState } from 'react';
import {
  Card, Input, Button, Space, Typography, Tag, App,
  Checkbox, Radio, Tooltip, TreeSelect,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  EditOutlined, CloudDownloadOutlined, SettingOutlined, CheckCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

// 分隔符:逗号 / 换行 / 空格 / 中文分号 / 中文逗号
const CODE_SEPARATORS = /[\s,;;，]+/;

function parseCodes(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const token of raw.split(CODE_SEPARATORS)) {
    const t = token.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

interface SceneAnnotateCardProps {
  /** 目标场景集 TreeSelect 的 treeData;由父页 (SceneGeneration) 复用同一份树。
   *  用 unknown 数组接收,与父页的 sceneSetTreeData useMemo 返回类型一致(不绑 antd 内部类型)。 */
  sceneSetTreeData: Record<string, unknown>[];
  /** 当前用户是否有 data:confidential:manage 权限;无则隐藏"保密"开关。 */
  canManageSecurity: boolean;
}

export default function SceneAnnotateCard({
  sceneSetTreeData,
  canManageSecurity,
}: SceneAnnotateCardProps) {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [codesInput, setCodesInput] = useState('');
  // 批次级选项,与「场景批量生成」保持默认值一致
  const [source, setSource] = useState<'jira' | 'case_flow'>('case_flow');
  const [confidential, setConfidential] = useState(false);
  const [sceneSetNodeId, setSceneSetNodeId] = useState<number | undefined>(undefined);

  const parsedCodes = parseCodes(codesInput);
  const codeCount = parsedCodes.length;

  const handleGoAnnotate = () => {
    if (codeCount === 0) {
      message.warning('请输入至少一个场景号');
      return;
    }
    if (codeCount > 200) {
      message.error(`场景号超过 200 条上限 (当前 ${codeCount});批量场景走"场景批量生成"卡片`);
      return;
    }
    const q = new URLSearchParams();
    q.set('codes', parsedCodes.join(','));
    q.set('source', source);
    if (canManageSecurity && confidential) q.set('confidential', '1');
    if (sceneSetNodeId != null) q.set('sceneSetNodeId', String(sceneSetNodeId));
    navigate(`/scene-annotate?${q.toString()}`);
  };

  return (
    <Card
      title={(
        <Space>
          <EditOutlined />
          场景标注
          <Tag color="processing" style={{ marginLeft: 4 }}>NEW</Tag>
        </Space>
      )}
      style={{ flex: '1 1 400px', minWidth: 320, display: 'flex', flexDirection: 'column' }}
      styles={{ body: { display: 'flex', flexDirection: 'column', flex: 1 } }}
    >
      {/* Radio: 来源二选一,默认 Case-Flow (与场景批量生成一致) */}
      <Radio.Group
        value={source}
        onChange={e => setSource(e.target.value)}
        optionType="button"
        buttonStyle="solid"
        size="small"
        style={{ marginBottom: 16 }}
        options={[
          { label: 'Case-Flow', value: 'case_flow' },
          { label: 'Jira', value: 'jira' },
        ]}
      />

      {/* 顶部流水线;首段随来源变化,后两段固定 */}
      <div style={{
        marginBottom: 20, padding: '12px 16px',
        background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0',
        display: 'flex', alignItems: 'center', gap: 32,
      }}>
        <Space size={4}>
          <CloudDownloadOutlined style={{ color: '#5e6ad2' }} />
          <Text type="secondary" style={{ fontSize: 13 }}>
            {source === 'jira' ? '拉取 Jira 数据' : '拉取 Case 数据'}
          </Text>
        </Space>
        <Text type="secondary" style={{ fontSize: 13 }}>→</Text>
        <Space size={4}>
          <SettingOutlined style={{ color: '#5e6ad2' }} />
          <Text type="secondary" style={{ fontSize: 13 }}>逐条标注</Text>
        </Space>
        <Text type="secondary" style={{ fontSize: 13 }}>→</Text>
        <Space size={4}>
          <CheckCircleOutlined style={{ color: '#5e6ad2' }} />
          <Text type="secondary" style={{ fontSize: 13 }}>统一提交</Text>
        </Space>
      </div>

      {/* 输入区:多行 textarea,支持逗号 / 换行 / 空格 分隔 */}
      <div style={{ marginBottom: 8 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          {source === 'jira' ? 'Jira Issue Key' : 'Case ID'}
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400, marginLeft: 8 }}>
            支持逗号 / 换行 / 空格 分隔
          </Text>
        </Text>
        <Input.TextArea
          value={codesInput}
          onChange={(e) => setCodesInput(e.target.value)}
          placeholder={source === 'jira'
            ? '例如:\nPROJ-1234\nPROJ-1235 PROJ-1236'
            : '例如:\nXDT0013_20260702101317\nXDT0014_20260702101318'}
          autoSize={{ minRows: 3, maxRows: 6 }}
          style={{ maxWidth: 600 }}
          allowClear
        />
        <div style={{ marginTop: 6, fontSize: 12, color: '#8c8c8c' }}>
          共解析出 <b style={{ color: '#5e6ad2' }}>{codeCount}</b> 条
        </div>
      </div>

      {/* 选项区:保密 (标注不需要"立即构建"选项,提交是显式动作) */}
      {canManageSecurity && (
        <div style={{ marginBottom: 16 }}>
          <Space size={16} wrap>
            <Space size={4}>
              <Checkbox
                checked={confidential}
                onChange={e => setConfidential(e.target.checked)}
              >
                保密
              </Checkbox>
              <Tooltip title="勾选后新建的场景被打为保密(security_level=1),仅具备 data:confidential:view 的角色可见;默认不勾选,普通场景所有登录用户可见。">
                <InfoCircleOutlined style={{ color: '#999' }} />
              </Tooltip>
            </Space>
          </Space>
        </div>
      )}

      {/* 目标场景集 */}
      <div style={{ marginBottom: 16 }}>
        <Space size={4} style={{ marginBottom: 8 }}>
          <Text strong>目标场景集</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>(可选)</Text>
          <Tooltip title="选择一个场景集后,标注提交、场景生成完成、进入「质检中」时会自动加入该场景集。只能选择场景集,不能选择目录。">
            <InfoCircleOutlined style={{ color: '#999' }} />
          </Tooltip>
        </Space>
        <TreeSelect
          style={{ maxWidth: 600, width: '100%' }}
          treeData={sceneSetTreeData}
          value={sceneSetNodeId}
          onChange={value => setSceneSetNodeId(value as number | undefined)}
          placeholder="不选择则不自动入集"
          allowClear
          showSearch
          treeNodeFilterProp="title"
          treeDefaultExpandAll={false}
          popupMatchSelectWidth={false}
        />
      </div>

      {/* 底部提交按钮;右对齐布局对齐场景批量生成 */}
      <div style={{
        marginTop: 'auto',
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1 }} />
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={handleGoAnnotate}
          disabled={codeCount === 0}
        >
          场景标注 ({codeCount})
        </Button>
      </div>
    </Card>
  );
}
